import { useEffect, useMemo, useState } from "react";
import { isIntraday, isIntradayInterval, rangeDays, type Interval, type Range } from "./indicators";

export type Transaction = {
  valueAt: string;
  avgValue: number;
  totalValue: number;
  totalQuantity: number;
  transactionsCount: number;
  /** Filled in over a stretch the server wasn't polling, rather than observed. */
  estimated?: boolean;
};

export type PricePoint = { date: string; price: number };

/**
 * The production fields are only on the items a company can be set to make, so
 * everything past the code and type is absent for a weapon or a case.
 */
export type MarketItem = {
  code: string;
  type: string;
  productionPoints?: number;
  productionNeeds?: Record<string, number>;
  isDeposit?: boolean;
  climates?: string[];
};

export function itemIcon(code: string) {
  return `https://media.warera.io/images/items/${code}.png?v=33`;
}

/** `?? 0` lets NaN through, and NaN is what a malformed number usually arrives as. */
export function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * A single unusable day would otherwise poison the chart's whole scale — one
 * NaN price turns the axis labels, the line and the candles into NaN — so drop
 * the days that can't be drawn and chart the rest.
 */
export function usableTransactions(values: unknown): Transaction[] {
  if (!Array.isArray(values)) return [];
  return values
    .filter((t): t is Transaction => isFiniteNumber(t?.avgValue) && typeof t?.valueAt === "string")
    .map((t) => ({ ...t, totalQuantity: isFiniteNumber(t.totalQuantity) ? t.totalQuantity : 0 }));
}

/**
 * The server's own record rather than upstream's: upstream publishes 30 days of
 * daily averages and nothing finer, while this reaches back as far as the
 * poller has been running and down to the five minutes between polls.
 */
export function historyPath(itemCode: string, range: Range, interval: Interval = "1d"): string {
  const days = rangeDays(range);
  const params = new URLSearchParams({ itemCode });
  if (days !== null) params.set("days", String(days));
  // A candle narrower than a day has to be built out of the polled snapshots,
  // whatever the range asks for, and the server groups them into candles this wide.
  if (isIntraday(range) || isIntradayInterval(interval)) {
    params.set("intraday", "1");
    params.set("interval", interval);
  }
  return `/api/history?${params}`;
}

/** The bars, plus when the poller first saw the item — null if it never has. */
export type History = { transactions: Transaction[]; firstIntradayAt: number | null };

export async function fetchHistory(
  itemCode: string,
  range: Range,
  interval: Interval = "1d",
): Promise<History> {
  const res = await fetch(historyPath(itemCode, range, interval));
  const json = await res.json();
  if (json.error) throw new Error(json.error.message);
  // Reject an unrecognised shape rather than reading it as "never traded" —
  // an empty chart and a broken API should not look the same.
  if (!Array.isArray(json?.bars)) throw new Error("Price history came back in an unexpected shape");
  return {
    transactions: usableTransactions(json.bars),
    firstIntradayAt: isFiniteNumber(json.firstIntradayAt) ? json.firstIntradayAt : null,
  };
}

export async function fetchTransactionHistory(
  itemCode: string,
  range: Range,
  interval: Interval = "1d",
): Promise<Transaction[]> {
  return (await fetchHistory(itemCode, range, interval)).transactions;
}

export type Mover = { code: string; changePct: number };

/**
 * Days are only recorded when an item traded, so "seven days ago" is the record
 * a week back rather than seven entries back. Anything without both ends of the
 * window has no move to report. The server runs this over its own records now,
 * so the ticker means the same thing wherever it is computed.
 */
export function weeklyChangePct(transactions: Transaction[], days = 7): number | null {
  const last = transactions[transactions.length - 1];
  if (!last) return null;

  const cutoff = Date.parse(last.valueAt) - days * 24 * 60 * 60 * 1000;
  const earlier =
    transactions.filter((t) => Date.parse(t.valueAt) <= cutoff).pop() ?? transactions[0];
  if (!earlier || earlier === last || !earlier.avgValue) return null;

  return ((last.avgValue - earlier.avgValue) / earlier.avgValue) * 100;
}

/**
 * One request for the whole strip: the server already holds every item's week
 * and compares them there, where it used to be one batched upstream call per
 * page load.
 */
export async function fetchWeeklyMovers(itemCodes: string[]): Promise<Mover[]> {
  if (!itemCodes.length) return [];

  const res = await fetch("/api/movers");
  const json = await res.json();
  if (!Array.isArray(json?.movers))
    throw new Error("Weekly movers came back in an unexpected shape");

  // The strip only shows what the picker lists, so an item the record still
  // holds but the game no longer trades stays off it.
  const listed = new Set(itemCodes);
  return (json.movers as Mover[]).filter(
    (mover) => listed.has(mover.code) && isFiniteNumber(mover.changePct),
  );
}

export function useWeeklyMovers(itemCodes: string[]): Mover[] {
  const [movers, setMovers] = useState<Mover[]>([]);
  const key = itemCodes.join(",");

  useEffect(() => {
    if (!key) return;
    let cancelled = false;

    fetchWeeklyMovers(key.split(","))
      .then((values) => {
        if (!cancelled) setMovers(values);
      })
      // The ticker is a garnish; a failure leaves it empty rather than shouting.
      .catch((err) => console.error("[ticker] weekly movers failed to load:", err));

    return () => {
      cancelled = true;
    };
  }, [key]);

  return movers;
}

export function toPriceHistory(transactions: Transaction[]): PricePoint[] {
  return transactions.map((t) => ({ date: t.valueAt, price: t.avgValue }));
}

export function useTransactionHistory(itemCode: string, range: Range, interval: Interval = "1d") {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [firstIntradayAt, setFirstIntradayAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Switching item makes the bars on screen wrong outright. Switching range
  // only asks the same story at another resolution, so those stay up until the
  // new ones land rather than blinking through an empty chart.
  useEffect(() => {
    setTransactions([]);
    setFirstIntradayAt(null);
  }, [itemCode]);

  useEffect(() => {
    setError(null);
    if (!itemCode) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchHistory(itemCode, range, interval)
      .then((history) => {
        if (cancelled) return;
        setTransactions(history.transactions);
        setFirstIntradayAt(history.firstIntradayAt);
      })
      .catch((err) => {
        if (cancelled) return;
        // Keeping the last range's bars up here would label a failed request as
        // a successful one, so a failure clears them and says so instead.
        setTransactions([]);
        setError(err as Error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [itemCode, range, interval]);

  return { transactions, firstIntradayAt, loading, error };
}

export function usePriceHistory(itemCode: string, range: Range, interval: Interval = "1d") {
  const { transactions, loading, error } = useTransactionHistory(itemCode, range, interval);
  const prices = useMemo(() => toPriceHistory(transactions), [transactions]);
  return { prices, loading, error };
}

/** Codes arrive as camelCase identifiers — "lightAmmo", "helmet1" — and the UI wants words. */
export function itemLabel(code: string): string {
  return code.replace(/([a-z])([A-Z0-9])/g, "$1 $2").replace(/^./, (first) => first.toUpperCase());
}

export async function fetchItems(): Promise<MarketItem[]> {
  const res = await fetch("/api/trpc/gameConfig.getGameConfig", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  });
  const json = await res.json();
  if (json.error) throw new Error(json.error.message);
  return itemsFromConfig(json?.result?.data);
}

/** The game config lists every item; only the tradable ones belong on a market page. */
export function itemsFromConfig(config: unknown): MarketItem[] {
  const items = (config as { items?: unknown } | undefined)?.items as
    | Record<
        string,
        Omit<MarketItem, "code" | "type"> & {
          isTradable?: boolean;
          type?: string;
        }
      >
    | undefined;
  if (!items) throw new Error("Item list came back in an unexpected shape");
  return Object.keys(items)
    .filter((code) => items[code]?.isTradable)
    .sort()
    .map((code) => {
      const { productionPoints, productionNeeds, isDeposit, climates } = items[code]!;
      return {
        code,
        type: items[code]?.type ?? "",
        productionPoints,
        productionNeeds,
        isDeposit,
        climates,
      };
    });
}

/** Both pages are built out of this list, so both fetch it the same way. */
export function useItems() {
  const [items, setItems] = useState<MarketItem[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);

    fetchItems()
      .then((listed) => {
        if (!cancelled) setItems(listed);
      })
      .catch((err) => {
        if (cancelled) return;
        // Without this the page rendered as a healthy but empty shell: an empty
        // picker, and three panels each reporting "no data for this item".
        setItems([]);
        setError(err as Error);
      });

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  return { items, error, retry: () => setAttempt((current) => current + 1) };
}
